import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import type { CommunityCampaign, UpsertCommunityCampaignInput } from '@/types/campaigns';
import { getErrorStatus } from '@/utils/errors';

interface CampaignRow {
  id: string | number | null;
  community_id: unknown;
  is_active?: boolean | null;
  title?: string | null;
  message?: string | null;
  image_path?: string | null;
  page?: string | null;
  finish_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const CAMPAIGNS_BUCKET = 'barrio-hub-manager';
const CAMPAIGNS_DIRECTORY = 'campaigns';

const parseCommunityIds = (value: CampaignRow['community_id']): string[] => {
  if (!value) {
    return [];
  }

  const candidates = Array.isArray(value) ? value : [value];

  const normalized = candidates
    .map(item => {
      if (typeof item === 'string' || typeof item === 'number') {
        const id = String(item).trim();
        return id.length > 0 ? id : null;
      }
      return null;
    })
    .filter((id): id is string => Boolean(id));

  return Array.from(new Set(normalized));
};

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const parseOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toCampaignRow = (value: unknown): CampaignRow | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    id:
      typeof value.id === 'string' || typeof value.id === 'number'
        ? value.id
        : null,
    community_id: value.community_id,
    is_active: value.is_active === true ? true : value.is_active === false ? false : null,
    title: typeof value.title === 'string' ? value.title : null,
    message: typeof value.message === 'string' ? value.message : null,
    image_path: typeof value.image_path === 'string' ? value.image_path : null,
    page: typeof value.page === 'string' ? value.page : null,
    finish_at: typeof value.finish_at === 'string' ? value.finish_at : null,
    created_at: typeof value.created_at === 'string' ? value.created_at : null,
    updated_at: typeof value.updated_at === 'string' ? value.updated_at : null,
  };
};

const mapCampaignRow = (row: CampaignRow | null): CommunityCampaign | null => {
  if (!row) {
    return null;
  }

  const id = row.id !== null && row.id !== undefined ? String(row.id) : null;
  const communityIds = parseCommunityIds(row.community_id);
  const imagePath = parseOptionalString(row.image_path);
  const page = parseOptionalString(row.page);
  const finishAt = parseOptionalString(row.finish_at);

  if (!id || communityIds.length === 0) {
    return null;
  }

  return {
    id,
    communityIds,
    isActive: row.is_active === true,
    title: row.title ?? null,
    message: row.message ?? '',
    imagePath,
    page,
    finishAt,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
};

const mapMany = (rows: CampaignRow[] | null | undefined): CommunityCampaign[] => {
  if (!rows || rows.length === 0) {
    return [];
  }

  return rows
    .map(mapCampaignRow)
    .filter((campaign): campaign is CommunityCampaign => Boolean(campaign));
};

const SELECT_COLUMNS = [
  'id',
  'community_id',
  'is_active',
  'title',
  'message',
  'image_path',
  'page',
  'finish_at',
  'created_at',
  'updated_at',
];

const SUPABASE_SELECT_CLAUSE = SELECT_COLUMNS.join(', ');

const sanitizeCommunityIds = (communityIds: string[] | undefined | null): string[] => {
  if (!communityIds || communityIds.length === 0) {
    return [];
  }

  return Array.from(
    new Set(
      communityIds
        .map(id => (typeof id === 'string' ? id.trim() : String(id ?? '')))
        .filter(id => id.length > 0)
    )
  );
};

const fetchCampaigns = async (
  options: { onlyActive?: boolean; communityIds?: string[] } = {}
): Promise<CommunityCampaign[]> => {
  await ensureAuthenticatedSession();

  let query = supabase
    .from('community_campaigns')
    .select(SUPABASE_SELECT_CLAUSE)
    .order('updated_at', { ascending: false, nullsFirst: false });

  if (options.onlyActive) {
    query = query.eq('is_active', true);
  }

  const filteredCommunityIds = sanitizeCommunityIds(options.communityIds);
  if (filteredCommunityIds.length === 1) {
    query = query.contains('community_id', JSON.stringify(filteredCommunityIds));
  } else if (filteredCommunityIds.length > 1) {
    const jsonConditions = filteredCommunityIds
      .map(id => `community_id.cs.[${JSON.stringify(id)}]`)
      .join(',');
    query = query.or(jsonConditions);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('No se pudieron consultar las campañas en Supabase.', error);
    throw new Error('No se pudieron obtener las campañas publicitarias.');
  }

  const rawData = data as unknown;
  const rows = Array.isArray(rawData)
    ? rawData.map(toCampaignRow).filter((row): row is CampaignRow => Boolean(row))
    : [];
  return mapMany(rows);
};

const sanitizeUpsertPayload = (input: UpsertCommunityCampaignInput, timestamp: string) => ({
  id: input.id ?? undefined,
  community_id: input.communityIds,
  is_active: input.isActive,
  title: input.title?.trim() || null,
  message: input.message.trim(),
  image_path: input.imagePath?.trim() || null,
  page: input.page?.trim() || null,
  finish_at: input.finishAt?.trim() || null,
  updated_at: timestamp,
});

const generateRandomId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `cmp-${Math.random().toString(36).slice(2)}${Date.now()}`;
};

const inferExtensionFromMime = (mime: string): string | null => {
  switch (mime) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    default:
      return null;
  }
};

const inferFileExtension = (file: File | Blob): string => {
  if ('name' in file && typeof file.name === 'string' && file.name.includes('.')) {
    const parts = file.name.split('.');
    const candidate = parts[parts.length - 1]?.trim().toLowerCase();
    if (candidate) {
      return candidate;
    }
  }

  const mimeExtension = inferExtensionFromMime(file.type);
  if (mimeExtension) {
    return mimeExtension;
  }

  return 'jpg';
};

const buildCampaignImagePath = (extension: string): string => {
  const normalizedExtension = extension.replace(/^[.]+/, '').toLowerCase();
  return `${CAMPAIGNS_DIRECTORY}/${generateRandomId()}.${normalizedExtension}`;
};

const getMimeTypeForExtension = (extension: string): string => {
  switch (extension.toLowerCase()) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    default:
      return 'image/jpeg';
  }
};

const removeImageIfNeeded = async (path: string | null) => {
  if (!path || isAbsoluteUrl(path)) {
    return;
  }

  await ensureAuthenticatedSession();

  const { error } = await supabase.storage.from(CAMPAIGNS_BUCKET).remove([path]);
  if (error) {
    throw new Error(error.message || 'No se pudo eliminar la imagen anterior de la campaña.');
  }
};

export const getCommunityCampaigns = async (): Promise<CommunityCampaign[]> => fetchCampaigns();

export const getCampaignByCommunity = async (
  communityId: string
): Promise<CommunityCampaign | null> => {
  const sanitizedId = communityId?.trim();
  if (!sanitizedId) {
    return null;
  }

  const campaigns = await fetchCampaigns({ communityIds: [sanitizedId] });

  return campaigns.length > 0 ? campaigns[0] : null;
};

export const getActiveCampaignsForCommunity = async (
  communityId: string
): Promise<CommunityCampaign[]> => {
  const sanitizedId = communityId?.trim();
  if (!sanitizedId) {
    return [];
  }

  return fetchCampaigns({ onlyActive: true, communityIds: [sanitizedId] });
};

export const getActiveCampaignForCommunity = async (
  communityId: string
): Promise<CommunityCampaign | null> => {
  const campaigns = await getActiveCampaignsForCommunity(communityId);

  return campaigns.length > 0 ? campaigns[0] : null;
};

export const upsertCommunityCampaign = async (
  input: UpsertCommunityCampaignInput
): Promise<CommunityCampaign> => {
  if (!input.communityIds || input.communityIds.length === 0) {
    throw new Error('Es necesario indicar al menos una comunidad para configurar una campaña.');
  }

  const normalizedMessage = input.message?.trim() ?? '';
  if (input.isActive && normalizedMessage.length === 0) {
    throw new Error('El mensaje de la campaña es obligatorio cuando está activa.');
  }

  await ensureAuthenticatedSession();

  const uniqueCommunityIds = Array.from(new Set(input.communityIds.filter(Boolean)));

  const normalizedImagePath = input.imagePath?.trim() ?? null;

  const now = new Date().toISOString();

  const payload = sanitizeUpsertPayload(
    {
      ...input,
      communityIds: uniqueCommunityIds,
      message: normalizedMessage,
      imagePath: normalizedImagePath,
    },
    now
  );

  const { data, error } = await supabase
    .from('community_campaigns')
    .upsert(payload)
    .select(SUPABASE_SELECT_CLAUSE)
    .maybeSingle();

  if (error) {
    throw new Error(error.message || 'No se pudo guardar la configuración de la campaña.');
  }

  const campaign = mapCampaignRow(toCampaignRow(data));
  if (!campaign) {
    throw new Error('Los datos devueltos por Supabase no son válidos.');
  }

  return campaign;
};

export const deleteCommunityCampaign = async (campaignId: string): Promise<void> => {
  if (!campaignId) {
    return;
  }

  await ensureAuthenticatedSession();

  const { error } = await supabase
    .from('community_campaigns')
    .delete()
    .eq('id', campaignId);

  if (error) {
    throw new Error(error.message || 'No se pudo eliminar la campaña publicitaria.');
  }
};

export const uploadCampaignImage = async (
  file: File | Blob
): Promise<{ path: string }> => {
  if (!file || file.size === 0) {
    throw new Error('Selecciona un archivo de imagen válido para la campaña.');
  }

  const extension = inferFileExtension(file);
  await ensureAuthenticatedSession();

  const finalPath = buildCampaignImagePath(extension);
  const normalizedContentType =
    file.type && file.type.startsWith('image/') ? file.type : getMimeTypeForExtension(extension);

  const { error } = await supabase.storage
    .from(CAMPAIGNS_BUCKET)
    .upload(finalPath, file, {
      contentType: normalizedContentType,
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    const message = error.message || '';
    throw new Error(message || 'No se pudo subir la imagen de la campaña.');
  }

  return { path: finalPath };
};

export const downloadCampaignImage = async (path: string): Promise<Blob> => {
  if (!path) {
    throw new Error('La ruta de la imagen de la campaña no es válida.');
  }

  if (isAbsoluteUrl(path)) {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error('No se pudo obtener la imagen de la campaña.');
    }
    return await response.blob();
  }

  const normalizedPath = path.replace(/^\/+/, '');
  await ensureAuthenticatedSession();

  const { data, error } = await supabase.storage
    .from(CAMPAIGNS_BUCKET)
    .download(normalizedPath);

  if (error || !data) {
    const message = error?.message ?? '';
    if (/Not Found/i.test(message) || getErrorStatus(error) === 404) {
      throw new Error('La imagen de la campaña no existe o fue eliminada.');
    }
    throw new Error(message || 'No se pudo obtener la imagen de la campaña.');
  }

  return data;
};

export const removeCampaignImage = async (path: string | null): Promise<void> => {
  if (!path || isAbsoluteUrl(path)) {
    return;
  }

  try {
    await removeImageIfNeeded(path);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('No se pudo eliminar la imagen seleccionada de la campaña.');
  }
};
